// src/taskpane/modules/engine.ts
// [Service Layer] AI Streaming Engine
// 负责处理流式请求、标签解析、思考/正文分流以及缓冲区清洗

import { EngineConfig } from "./store";
import { Logger } from "./view"; 

// AbortController 降级兼容性声明
/* global AbortController */

export class AIStreamEngine {
    private config: EngineConfig;
    private controller: AbortController;
    
    // --- 数据缓冲区 ---
    private streamBuffer: string = "";   // 原始流碎片拼接区
    private contentBuffer: string = "";  // 待处理的有效文本区
    
    // --- 状态标记 ---
    private isFreshStart: boolean = true;      // 整个请求是否刚开始
    private isZoneStart: boolean = true;       // [关键] 当前区域(Thought/Content)是否刚开始
    private activeZone: "thought" | "content" = "thought";
    private lang: "zh" | "en" = "zh"; // [新增] 任务语言状态
    
    // --- 正则定义 ---
    // 匹配完整标签：===THOUGHT===
    private readonly FULL_TAG_REGEX = /={2,}\s*(THOUGHT|CONTENT|END_THOUGHT|END_CONTENT)\s*={2,}/i;
    // 匹配不完整标签（用于防止标签被切断）：===THOUGH
    private readonly PARTIAL_TAG_REGEX = /={2,}$|={2,}\s*([A-Z_]*)$/i;
    
    // --- 看门狗 (超时检测) ---
    private watchdogTimer: number | undefined;
    private lastPacketTime: number = 0;
    private readonly TIMEOUT_MS = 120000; // 2分钟超时
    private isTimeoutTriggered = false;
    private isManualAbort = false;

    constructor(config: EngineConfig) { 
        this.config = config; 
        // AbortController 降级兼容性保护
        if (typeof AbortController !== 'undefined') {
            this.controller = new AbortController();
        } else {
            // 降级方案：创建一个模拟的 AbortController
            const mockSignal = {
                aborted: false,
                onabort: null as (() => void) | null,
                addEventListener: () => {},
                removeEventListener: () => {},
                dispatchEvent: () => true
            };
            
            const mockController = {
                signal: mockSignal,
                abort: () => {
                    mockSignal.aborted = true;
                    if (mockSignal.onabort) {
                        mockSignal.onabort();
                    }
                }
            };
            
            this.controller = mockController as any;
            Logger.info("[Engine] AbortController 不可用，使用降级方案");
        }
    }

    // --- 核心方法: 启动请求 ---
    public async start(payload: any, externalSignal?: AbortSignal): Promise<void> {
        this.lang = payload.lang || "zh"; // [关键新增] 在请求启动时捕获当前语言环境
        // [健壮性补丁] 确保旧的看门狗计时器被强制销毁，防止多个看门狗并行导致意外的超时中断
        this.stopWatchdog();
        this.startWatchdog();
        try {
            // 合并信号：外部信号优先，内部信号作为后备
            const signals = [];
            if (externalSignal) signals.push(externalSignal);
            signals.push(this.controller.signal);
            
            const response = await fetch(this.config.url, {
                method: "POST", 
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload), 
                signal: this.mergeSignals(signals)
            });
            
            if (!response.ok) {
                let errMsg = `HTTP ${response.status}`;
                try { 
                    const json = await response.json(); 
                    if (json.error?.message) errMsg = json.error.message; 
                } catch (e) { 
                    errMsg = await response.text(); 
                }
                throw new Error(errMsg); 
            }
            
            if (!response.body) throw new Error("Stream not supported by browser");
            
            const reader = response.body.getReader(); 
            const decoder = new TextDecoder("utf-8");
            
            // 初始化状态
            this.config.onTransition("thinking"); 
            this.activeZone = "thought"; 
            this.streamBuffer = ""; 
            this.contentBuffer = ""; 
            this.isFreshStart = true;
            this.isZoneStart = true; 
            
            while (true) {
                const { done, value } = await reader.read();
                this.tick(); 
                if (done) break;
                
                const chunkText = decoder.decode(value, { stream: true });
                this.streamBuffer += chunkText;
                
                // 处理 SSE 数据行 (data: ...)
                const lines = this.streamBuffer.split("\n");
                this.streamBuffer = lines.pop() || ""; // 保留最后一行未完结的内容

                for (const line of lines) {
                    const trimmed = line.trim();
                    if (!trimmed.startsWith("data: ")) continue;
                    if (trimmed === "data: [DONE]") continue;
                    try {
                        const json = JSON.parse(trimmed.substring(6));
                        const delta = json.choices[0].delta.content || json.choices[0].delta.reasoning_content || "";
                        if (delta) { 
                            // 处理每一个增量包
                            if (!this.processDelta(delta)) { 
                                reader.cancel(); 
                                return; 
                            } 
                        }
                    } catch (e) { /* 忽略 JSON 解析错误 */ }
                }
            }
            const finalChunk = decoder.decode(); 
            if (finalChunk) this.processDelta(finalChunk);
            
            this.processFinalBuffer();

        } catch (e) {
            if (e.name === 'AbortError') { 
                if (!this.isManualAbort) this.flushRemainingBuffer(); 
            } else { 
                throw e; 
            }
        } finally { 
            this.stopWatchdog(); 
        }
    }
    
    public abort() { 
        this.isManualAbort = true; 
        this.stopWatchdog(); 
        this.controller.abort(); 
    }

    // --- 核心方法: 增量处理 (Buffer 清洗逻辑在此) ---
    private processDelta(delta: string): boolean {
        // 调试日志
        if (this.isFreshStart || delta.includes("#")) {
            Logger.info(`[Engine] Raw Delta: ${JSON.stringify(delta)}`);
        }
        // 调试日志
        this.contentBuffer += delta;
        
        // 1. [启动清洗] 去除 可能输出的 Markdown 代码块头等垃圾
        if (this.isFreshStart) {
            const garbageList = ["----格式开始----", "----", "【最终输出格式严格示例】", "【全局安全协议】", "```markdown", "```"];
            let cleaned = true;
            while (cleaned && this.contentBuffer.length > 0) {
                cleaned = false;
                for (const trash of garbageList) {
                    if (this.contentBuffer.startsWith(trash)) { 
                        this.contentBuffer = this.contentBuffer.substring(trash.length); 
                        cleaned = true; 
                    }
                }
                const scrubbed = this.contentBuffer.replace(/^[ \t\r\n_\-]+/, ""); 
                if (scrubbed.length !== this.contentBuffer.length) { 
                    this.contentBuffer = scrubbed; 
                    cleaned = true; 
                }
            }
            if (this.contentBuffer.length === 0) return true; // 还没攒够有效字符
            
            // 只要不是标签开头，就说明内容开始了
            if (!this.contentBuffer.startsWith("=")) { 
                this.isFreshStart = false; 
            }
        }

        // 2. [标签解析] 循环查找 ===THOUGHT=== 等标签
        let match;
        while ((match = this.contentBuffer.match(this.FULL_TAG_REGEX)) !== null) {
            const fullTagStr = match[0];
            const tagName = match[1].toUpperCase();
            const tagIndex = match.index!;
            
            // 如果标签前面还有内容，先发射出去
            if (tagIndex > 0) { 
                const textBefore = this.contentBuffer.substring(0, tagIndex); 
                this.emitContent(textBefore); 
            }
            
            // 切换区域状态
            if (tagName === "THOUGHT") { 
                this.activeZone = "thought"; 
                this.config.onTransition("thinking"); 
                this.isZoneStart = true; // [标记] 思考区域刚开始
            } 
            else if (tagName === "CONTENT" || tagName === "END_THOUGHT") { 
                this.activeZone = "content"; 
                this.config.onTransition("writing"); 
                this.isZoneStart = true; // [标记] 正文区域刚开始
            } 
            else if (tagName === "END_CONTENT") { 
                return false; // 结束信号
            }

            // 从 Buffer 中移除已处理的标签
            this.contentBuffer = this.contentBuffer.substring(tagIndex + fullTagStr.length);
            this.isFreshStart = false;
        }

        // 3. [Buffer 级强力清洗] 首行修剪
        // 如果当前处于“区域开始”状态 (isZoneStart)，直接在源头砍掉开头的空白/换行
        if (this.isZoneStart) {
            const trimmed = this.contentBuffer.trimStart();
            if (trimmed.length === 0) {
                this.contentBuffer = "";
                return true; 
            }
            this.contentBuffer = trimmed;
            this.isZoneStart = false;
        }

        // 4. [发送剩余内容]
        if (this.contentBuffer) {
            const isPotentialTag = this.PARTIAL_TAG_REGEX.test(this.contentBuffer);
            if (isPotentialTag) { 
                if (this.contentBuffer.length < 20) return true; 
            }
            
            this.emitContent(this.contentBuffer);
            this.contentBuffer = "";
        }
        return true;
    }

    // --- 核心方法: 内容发送 (中英文感知) ---
    private emitContent(text: string) {
        if (!text) return;
        
        // 1. 拦截纯垃圾字符 (避免无意义输出)
        const isPureGarbage = /^[=_\-\s]+$/.test(text);
        if (isPureGarbage) return;

        let cleanText = text;

        // 2. 通用格式清洗
        cleanText = cleanText.replace(/\r\n/g, "\n"); // 统一换行符标准

        // [核心业务逻辑：中英文感知型清洗] 
        // 针对中文移除行首缩进(左对齐)，针对英文仅清除全角字符并保留半角空格以维持阅读性
        if (this.lang !== "en") {
            // 中文模式：激进清洗行首空白，优化预览区对齐效果
            cleanText = cleanText.replace(/^[ \t\u3000]+/gm, "");
        } else {
            // 英文模式：严禁删除行首半角空格，仅处理全角空格 (Unicode: \u3000)
            cleanText = cleanText.replace(/\u3000/g, "");
        }

        cleanText = cleanText.replace(/\n{3,}/g, "\n\n"); // 压缩多余空行

        // 3. 根据区域路由输出
        if (this.activeZone === "thought") { 
            this.config.onThought(cleanText); 
        } else { 
            this.config.onContent(cleanText); 
        } 
    }

    // --- 辅助方法 ---
    // 合并多个 AbortSignal
    private mergeSignals(signals: AbortSignal[]): AbortSignal {
        if (signals.length === 0) {
            return new AbortController().signal; // 返回一个永远不会中止的信号
        }
        if (signals.length === 1) {
            return signals[0];
        }
        
        const controller = new AbortController();
        const onAbort = () => {
            controller.abort();
        };
        
        signals.forEach(signal => {
            if (signal.aborted) {
                controller.abort();
            } else {
                signal.addEventListener('abort', onAbort);
            }
        });
        
        // 清理事件监听器
        controller.signal.addEventListener('abort', () => {
            signals.forEach(signal => {
                signal.removeEventListener('abort', onAbort);
            });
        });
        
        return controller.signal;
    }

    // 处理最终 Buffer (防止内容丢失)
    private processFinalBuffer() {
         if (this.contentBuffer) {
             const isPureGarbage = /^[ \t\r\n=_\-]+$/.test(this.contentBuffer);
             if (!isPureGarbage) this.emitContent(this.contentBuffer); 
        } 
    }
    // 清空剩余缓冲区
    private flushRemainingBuffer() { 
        this.processFinalBuffer(); 
        this.contentBuffer = ""; 
    }
    // 启动看门狗 (超时检测)
    private startWatchdog() {
        this.stopWatchdog(); 
        this.lastPacketTime = Date.now(); 
        this.isTimeoutTriggered = false;
        this.watchdogTimer = window.setInterval(() => {
            if (Date.now() - this.lastPacketTime > this.TIMEOUT_MS && !this.controller.signal.aborted) {
                console.warn(`[AIStream] Timeout detected, aborting...`); 
                this.isTimeoutTriggered = true; 
                this.controller.abort();
            }
        }, 1000);
    }
    // 停止看门狗
    private stopWatchdog() { 
        if (this.watchdogTimer) { 
            clearInterval(this.watchdogTimer); 
            this.watchdogTimer = undefined; 
        } 
    }
    // 看门狗点位 (收到数据包时调用)
    private tick() { 
        this.lastPacketTime = Date.now(); 
    }
}
